import personPlaceholder from '@alltrails/images/placeholder/person_placeholder.png';
import { LanguageSupportUtil } from './language_support_util';
import { StringConstantsMixin } from '../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { getAmplitudeInstance } from './amplitude_helpers';
import {
  CREATE_MAP_NAV_BUTTON_TAPPED,
  FAVORITES_NAV_BUTTON_TAPPED,
  LISTS_NAV_BUTTON_TAPPED,
  MAPS_NAV_BUTTON_TAPPED
} from './constants/AmplitudeEvent';

const { wrapUrlSafe } = LanguageSupportUtil;
const {
  strings: {
    NAVIGATION: {
      LOGIN,
      SIGNUP,
      LOGOUT,
      SETTINGS,
      MEMBERS,
      PHOTOS,
      REVIEWS,
      PROFILE,
      ACTIVITIES,
      UPGRADE_TO_PRO,
      EXPLORE,
      PLAN,
      FAVORITES,
      LISTS,
      MAPS,
      CREATE_MAP,
      HISTORY,
      COMMUNITY,
      COMPLETED,
      STATS,
      HELP,
      DOWNLOAD_THE_APP,
      SAVED
    }
  }
} = StringConstantsMixin;

const favorites = (languageRegionCode, slug) => ({
  label: FAVORITES,
  linkPath: slug ? wrapUrlSafe(`/members/${slug}/favorites?ref=header`, languageRegionCode) : wrapUrlSafe('/signup?ref=header', languageRegionCode),
  onClick: () => {
    getAmplitudeInstance().logEvent(FAVORITES_NAV_BUTTON_TAPPED);
  }
});

const lists = (languageRegionCode, slug) => ({
  label: LISTS,
  linkPath: slug ? wrapUrlSafe(`/members/${slug}/lists?ref=header`, languageRegionCode) : wrapUrlSafe('/signup?ref=header', languageRegionCode),
  onClick: () => {
    getAmplitudeInstance().logEvent(LISTS_NAV_BUTTON_TAPPED);
  }
});

const maps = (languageRegionCode, slug) => ({
  label: MAPS,
  linkPath: slug
    ? wrapUrlSafe(`/members/${slug}/maps?ref=header-plan-my-maps`, languageRegionCode)
    : wrapUrlSafe('/signup?ref=header', languageRegionCode),
  onClick: () => {
    getAmplitudeInstance().logEvent(MAPS_NAV_BUTTON_TAPPED);
  }
});

const createMap = (languageRegionCode, slug) => ({
  label: CREATE_MAP,
  linkPath: slug ? wrapUrlSafe('/explore/map/new', languageRegionCode) : wrapUrlSafe('/signup?ref=header', languageRegionCode),
  onClick: () => {
    getAmplitudeInstance().logEvent(CREATE_MAP_NAV_BUTTON_TAPPED);
  }
});

const loggedInProps = ({ firstName, slug, reputation, profilePhoto }, languageRegionCode = 'en-US', proUser, forceProLogo = false) => {
  // use proUser from the context obj instead of "pro" from the currentUser object because private currentUser.pro is nil
  return {
    user: {
      name: firstName,
      linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode),
      imagePath:
        profilePhoto && profilePhoto.id && profilePhoto.photoHash
          ? `https://cdn-assets.alltrails.com/uploads/photo/image/${profilePhoto.id}/icon_${profilePhoto.photoHash}.jpg`
          : personPlaceholder,
      reputation,
      display: true,
      login: {
        label: LOGIN,
        linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
      },
      signup: {
        label: SIGNUP,
        linkPath: wrapUrlSafe('/signup?ref=header', languageRegionCode)
      },
      dropdown: [
        {
          label: PROFILE,
          linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode)
        },
        {
          label: STATS,
          linkPath: wrapUrlSafe(`/members/${slug}/stats?ref=header`, languageRegionCode)
        },
        {
          label: REVIEWS,
          linkPath: wrapUrlSafe(`/members/${slug}/reviews?ref=header`, languageRegionCode)
        },
        {
          label: PHOTOS,
          linkPath: wrapUrlSafe(`/members/${slug}/photos?ref=header`, languageRegionCode)
        },
        {
          label: MEMBERS,
          linkPath: wrapUrlSafe('/members?ref=header', languageRegionCode)
        },
        {
          label: SETTINGS,
          linkPath: wrapUrlSafe('/my/profile/edit?ref=header', languageRegionCode)
        },
        {
          label: LOGOUT,
          linkPath: wrapUrlSafe('/sign_out?ref=header', languageRegionCode),
          onClick: () => {
            const amplitudeInstance = getAmplitudeInstance();
            amplitudeInstance.setUserId(null);
            amplitudeInstance.regenerateDeviceId();
          }
        }
      ]
    },
    brand: {
      link: wrapUrlSafe('/?ref=header', languageRegionCode),
      logo: {
        linkPath: wrapUrlSafe('/?ref=header', languageRegionCode)
      },
      proBadge: {
        active: proUser || forceProLogo,
        linkPath: proUser ? wrapUrlSafe('/pro/welcome?ref=header', languageRegionCode) : wrapUrlSafe('/pro?ref=header', languageRegionCode),
        show: true
      }
    },
    proButton: {
      label: UPGRADE_TO_PRO,
      linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode),
      display: !proUser
    },
    links: {
      explore: {
        label: EXPLORE,
        linkPath: wrapUrlSafe('/explore?ref=header', languageRegionCode)
      },
      plan: {
        label: PLAN,
        linkPath: wrapUrlSafe(`/members/${slug}/favorites?ref=header`, languageRegionCode),
        dropdown: [
          favorites(languageRegionCode, slug),
          lists(languageRegionCode, slug),
          maps(languageRegionCode, slug),
          createMap(languageRegionCode, slug)
        ]
      },
      history: {
        label: HISTORY,
        linkPath: wrapUrlSafe(`/members/${slug}/recordings?ref=header`, languageRegionCode),
        dropdown: [
          {
            label: ACTIVITIES,
            linkPath: wrapUrlSafe(`/members/${slug}/recordings?ref=header`, languageRegionCode)
          },
          {
            label: COMPLETED,
            linkPath: wrapUrlSafe(`/members/${slug}/completed?ref=header`, languageRegionCode)
          },
          {
            label: STATS,
            linkPath: wrapUrlSafe(`/members/${slug}/stats?ref=header`, languageRegionCode),
            display: true
          }
        ]
      },
      help: {
        label: HELP,
        linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
        locale: true
      },
      downloadApp: {
        label: DOWNLOAD_THE_APP,
        linkPath: 'https://alltrails.io/BrtlItRPbo'
      }
    }
  };
};

const loggedOutProps = (languageRegionCode = 'en-US') => {
  const signUpPath = wrapUrlSafe('/signup?ref=header', languageRegionCode);

  return {
    user: {
      display: false,
      login: {
        label: LOGIN,
        linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
      },
      signup: {
        label: SIGNUP,
        linkPath: signUpPath
      },
      dropdown: []
    },
    brand: {
      link: wrapUrlSafe('/?ref=header', languageRegionCode),
      logo: {
        linkPath: wrapUrlSafe('/?ref=header', languageRegionCode)
      },
      proBadge: {
        active: false,
        linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode),
        show: true
      }
    },
    proButton: {
      label: UPGRADE_TO_PRO,
      linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode),
      display: false
    },
    links: {
      explore: {
        label: EXPLORE,
        linkPath: wrapUrlSafe('/explore?ref=header', languageRegionCode)
      },
      plan: {
        label: PLAN,
        linkPath: wrapUrlSafe(`/members/slug/favorites?ref=header`, languageRegionCode),
        dropdown: [favorites(languageRegionCode), lists(languageRegionCode), maps(languageRegionCode), createMap(languageRegionCode)]
      },
      history: {
        label: HISTORY,
        linkPath: signUpPath,
        dropdown: [
          {
            label: ACTIVITIES,
            linkPath: signUpPath
          },
          {
            label: COMPLETED,
            linkPath: signUpPath
          },
          {
            label: STATS,
            linkPath: signUpPath,
            display: true
          }
        ]
      },
      help: {
        label: HELP,
        linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
        locale: true
      },
      downloadApp: {
        label: DOWNLOAD_THE_APP,
        linkPath: 'https://alltrails.io/BrtlItRPbo'
      }
    }
  };
};

const communityLoggedInProps = ({ firstName, slug, reputation, profilePhoto }, languageRegionCode = 'en-US', proUser, forceProLogo = false) => {
  return {
    user: {
      name: firstName,
      linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode),
      imagePath:
        profilePhoto && profilePhoto.id && profilePhoto.photoHash
          ? `https://cdn-assets.alltrails.com/uploads/photo/image/${profilePhoto.id}/icon_${profilePhoto.photoHash}.jpg`
          : personPlaceholder,
      reputation,
      display: true,
      login: {
        label: LOGIN,
        linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
      },
      signup: {
        label: SIGNUP,
        linkPath: wrapUrlSafe('/signup?ref=header', languageRegionCode)
      },
      dropdown: [
        {
          label: PROFILE,
          linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode)
        },
        {
          label: STATS,
          linkPath: wrapUrlSafe(`/members/${slug}/stats?ref=header`, languageRegionCode)
        },
        {
          label: REVIEWS,
          linkPath: wrapUrlSafe(`/members/${slug}/reviews?ref=header`, languageRegionCode)
        },
        {
          label: PHOTOS,
          linkPath: wrapUrlSafe(`/members/${slug}/photos?ref=header`, languageRegionCode)
        },
        {
          label: MEMBERS,
          linkPath: wrapUrlSafe('/members?ref=header', languageRegionCode)
        },
        {
          label: SETTINGS,
          linkPath: wrapUrlSafe('/my/profile/edit?ref=header', languageRegionCode)
        },
        {
          label: LOGOUT,
          linkPath: wrapUrlSafe('/sign_out?ref=header', languageRegionCode)
        }
      ]
    },
    brand: {
      link: wrapUrlSafe('/?ref=header', languageRegionCode),
      logo: {
        linkPath: wrapUrlSafe('/?ref=header', languageRegionCode)
      },
      proBadge: {
        active: proUser || forceProLogo,
        linkPath: proUser ? wrapUrlSafe('/pro/welcome?ref=header', languageRegionCode) : wrapUrlSafe('/pro?ref=header', languageRegionCode),
        show: true
      }
    },
    proButton: {
      label: UPGRADE_TO_PRO,
      linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode),
      display: !proUser
    },
    links: {
      explore: {
        label: EXPLORE,
        linkPath: wrapUrlSafe('/explore?ref=header', languageRegionCode)
      },
      community: {
        label: COMMUNITY,
        linkPath: wrapUrlSafe('/members?ref=header', languageRegionCode)
      },
      saved: {
        label: SAVED,
        linkPath: '#', // TODO: what should this link be for SEO
        dropdown: [
          favorites(languageRegionCode, slug),
          maps(languageRegionCode, slug),
          lists(languageRegionCode, slug),
          {
            label: ACTIVITIES,
            linkPath: wrapUrlSafe(`/members/${slug}/recordings?ref=header`, languageRegionCode)
          },
          {
            label: COMPLETED,
            linkPath: wrapUrlSafe(`/members/${slug}/completed?ref=header`, languageRegionCode),
            underline: true
          },
          createMap(languageRegionCode, slug)
        ]
      },
      help: {
        label: HELP,
        linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
        locale: true
      },
      downloadApp: {
        label: DOWNLOAD_THE_APP,
        linkPath: 'https://alltrails.io/BrtlItRPbo'
      }
    }
  };
};

const communityLoggedOutProps = languageRegionCode => {
  const signUpPath = wrapUrlSafe('/signup?ref=header', languageRegionCode);

  return {
    user: {
      display: false,
      login: {
        label: LOGIN,
        linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
      },
      signup: {
        label: SIGNUP,
        linkPath: signUpPath
      },
      dropdown: []
    },
    brand: {
      link: wrapUrlSafe('/?ref=header', languageRegionCode),
      logo: {
        linkPath: wrapUrlSafe('/?ref=header', languageRegionCode)
      },
      proBadge: {
        active: false,
        linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode),
        show: true
      }
    },
    proButton: {
      label: UPGRADE_TO_PRO,
      linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode),
      display: false
    },
    links: {
      explore: {
        label: EXPLORE,
        linkPath: wrapUrlSafe('/explore?ref=header', languageRegionCode)
      },
      community: {
        label: COMMUNITY,
        linkPath: signUpPath
      },
      saved: {
        label: SAVED,
        linkPath: '#', // TODO: what should this link be for SEO
        dropdown: [
          favorites(languageRegionCode),
          maps(languageRegionCode),
          lists(languageRegionCode),
          {
            label: ACTIVITIES,
            linkPath: signUpPath
          },
          {
            label: COMPLETED,
            linkPath: signUpPath,
            underline: true
          },
          createMap(languageRegionCode)
        ]
      },
      help: {
        label: HELP,
        linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
        locale: true
      },
      downloadApp: {
        label: DOWNLOAD_THE_APP,
        linkPath: 'https://alltrails.io/BrtlItRPbo'
      }
    }
  };
};

const generateCommunityHeaderProps = (currentUser, languageRegionCode, proUser, forceProLogo) => {
  return currentUser ? communityLoggedInProps(currentUser, languageRegionCode, proUser, forceProLogo) : communityLoggedOutProps(languageRegionCode);
};

const generateStandardHeaderProps = (currentUser, languageRegionCode, proUser, forceProLogo) => {
  return currentUser ? loggedInProps(currentUser, languageRegionCode, proUser, forceProLogo) : loggedOutProps(languageRegionCode);
};

const generateNavigationProps = ({
  currentUser,
  languageRegionCode,
  proUser,
  featureFlags = { communityFeatureFlag: false },
  forceProLogo = false
}) => {
  return featureFlags.communityFeatureFlag
    ? generateCommunityHeaderProps(currentUser, languageRegionCode, proUser, forceProLogo)
    : generateStandardHeaderProps(currentUser, languageRegionCode, proUser, forceProLogo);
};

export { generateNavigationProps };
